import dayjs from 'dayjs';
import { Role } from '../enums';
import {
	ICandidateAdditionalInfo,
	ICandidateInfo,
	Identifiable,
	IEducation,
	IOnlinePresence,
	IProfileInfo,
	ISalary,
	ISkill,
	IWorkExperience,
	TAResponse
} from '../types';
import { CandidateType } from '../types/candidate-type';
import config from '../utils/config';
import ResourceManager from './resource.manager';
class CandidatesService extends ResourceManager {
	getCandidates(params: any = null, options: any = null): any {
		return this.getResource(config.URIs.get_candidates, {
			...(options || {}),
			params
		});
	}
	async getCandidatesTags(candidateType?: CandidateType): Promise<any> {
		return await this.getResource(config.URIs.get_candidates_tags, { params: { candidateType } });
	}
	async getCandidateProfileInfo(candidateId, params: any = null): Promise<ICandidateInfo | null> {
		const resp = await this.getResource<ICandidateInfo>(
			config.URIs.get_candidate.replace(/:candidateId/, candidateId),
			{ params }
		);
		if (resp && resp.profile.dateOfBirth)
			resp.profile.dateOfBirth = dayjs(new Date(resp.profile.dateOfBirth)).format('MMMM D, YYYY');
		return resp;
	}

	async setCandidateTags(candidateId, tags, source?: CandidateType): Promise<any> {
		return this.putResource(
			config.URIs.candidate_set_tags.replace(':candidateId', candidateId),
			{ tags },
			{ params: { source } }
		);
	}

	async updateAvatar(avatar: File, candidateId: string, source?: CandidateType) {
		const data = new FormData();
		data.append('avatar', avatar);
		const response = await this.patchResource<FormData, TAResponse<{ url: string }>>(
			config.URIs.update_avatar,
			data,
			{ params: { candidateId, source } }
		);
		return response?.data.url;
	}
	async deleteAvatar(candidateId: string, source?: CandidateType) {
		const response = await this.deleteResource<TAResponse<null>>(config.URIs.update_avatar, {
			params: { candidateId, source }
		});
		return response?.success;
	}
	async saveProfile(profile: IProfileInfo, candidateId: string, source?: CandidateType) {
		const response = await this.patchResource<IProfileInfo, TAResponse<Identifiable<IProfileInfo>>>(
			config.URIs.save_profile,
			profile,
			{ params: { candidateId, source } }
		);
		if (response && response.data.dateOfBirth)
			response.data.dateOfBirth = dayjs(new Date(response.data.dateOfBirth)).format('MMMM D, YYYY');
		return response?.data;
	}

	async lookingForJobs(candidateId, status, candidateType): Promise<any> {
		return this.postResource(
			config.URIs.candidate_toggle_looking,
			{ candidateId, status },
			{ params: { candidateType } }
		);
	}

	getCandidateApplications(candidateId) {
		return this.getResource(config.URIs.get_candidate_applications.replace(/:candidateId/g, candidateId), {});
	}

	async getCandidateCV(candidateId, candidateType): Promise<any> {
		return this.getResource(config.URIs.get_candidate_CV.replace(/:candidateId/g, candidateId), {
			responseType: 'arraybuffer',
			params: { candidateType }
		});
	}

	getCandidate(candidateId): Promise<ICandidateInfo | null> {
		return this.getResource<ICandidateInfo>(config.URIs.get_candidate.replace(/:candidateId/g, candidateId));
	}

	createCandidate(data) {
		return this.postResource(config.URIs.create_candidate, data);
	}

	updateCandidate(candidateId, data): Promise<any> {
		return this.putResource(config.URIs.update_candidate.replace(/:candidateId/g, candidateId), data);
	}

	deleteCandidateBackofficeCV(candidateId, source?: CandidateType) {
		return this.putResource(
			config.URIs.delete_candidate_backoffice_cv.replace(/:candidateId/g, candidateId),
			{},
			{ params: { source } }
		);
	}
	deleteCandidateCV(candidateId, source?: CandidateType) {
		return this.putResource(
			config.URIs.delete_candidate_cv.replace(/:candidateId/g, candidateId),
			{},
			{ params: { source } }
		);
	}

	deleteCandidateTechnicalReport(candidateId, report?: string, source?: CandidateType) {
		return this.putResource(
			config.URIs[report ? 'delete_custom_report_to_candidate' : 'delete_candidate_technical_report'].replace(
				/:candidateId/g,
				candidateId
			),
			{ report },
			{ params: { source } }
		);
	}

	getCandidateNextCode(): Promise<string[] | null> {
		return this.getResource(config.URIs.next_candidate_code);
	}

	checkIfEmailExists(email: string): Promise<{ exists: boolean } | null> {
		return this.getResource(config.URIs.candidate_email_exists, { params: { email } });
	}

	updateCandidateNotes(candidateId: string, notes: string, source?: CandidateType) {
		return this.putResource(
			config.URIs.update_candidate_notes.replace(/:candidateId/g, candidateId),
			{ notes },
			{ params: { source } }
		);
	}

	updateCandidateOurStatus(candidateId: string, ourStatus: string, source?: CandidateType) {
		return this.putResource(
			config.URIs.update_candidate_our_status.replace(/:candidateId/g, candidateId),
			{
				ourStatus
			},
			{ params: { source } }
		);
	}

	async addCandidateTechnicalReport(
		fd: FormData,
		candidateId,
		source?: CandidateType
	): Promise<TAResponse<string> | null> {
		const response = await this.putResource<FormData, TAResponse<string>>(
			config.URIs.add_candidate_technical_report.replace(/:candidateId/, candidateId),
			fd,
			{ params: { source } }
		);
		return response ?? null;
	}

	addCustomReport(candidateId, report, source?: CandidateType) {
		return this.putResource(
			config.URIs.add_custom_report_to_candidate.replace(/:candidateId/g, candidateId),
			report,
			{ params: { source } }
		);
	}

	async inviteDeveloper(developerEmail) {
		return await this.postResource(config.URIs.invite_developers, {
			email: developerEmail,
			role: Role.DEVELOPER_USER
		});
	}

	async resendDeveloperInvitation(invitationId) {
		return await this.putResource(
			config.URIs.resend_developer_invitation.replace(/:invitationId/g, invitationId),
			{}
		);
	}

	//====Education====

	async saveEducation(
		edu: IEducation,
		candidateId,
		source?: CandidateType
	): Promise<Identifiable<IEducation> | null> {
		const response = await this.patchResource<IEducation, TAResponse<Identifiable<IEducation>>>(
			config.URIs.save_education,
			edu,
			{ params: { candidateId, source } }
		);
		return response?.data ?? null;
	}

	async removeEducation(id: string, candidateId: string, source?: CandidateType): Promise<string | null> {
		const response = await this.patchResource<null, TAResponse<{ id: string }>>(
			config.URIs.remove_education.replace(/:educationId/, id),
			null,
			{ params: { candidateId, source } }
		);
		return response?.data.id ?? null;
	}

	async editEducation(
		id: string,
		edu: IEducation,
		candidateId,
		source?: CandidateType
	): Promise<Identifiable<IEducation> | null> {
		const response = await this.patchResource<IEducation, TAResponse<Identifiable<IEducation>>>(
			config.URIs.edit_education.replace(/:educationId/, id),
			edu,
			{ params: { candidateId, source } }
		);
		return response?.data ?? null;
	}

	//====Work Experience====

	async saveWorkExperience(
		exp: IWorkExperience,
		candidateId: string,
		source?: CandidateType
	): Promise<Identifiable<IWorkExperience> | null> {
		const response = await this.patchResource<IWorkExperience, TAResponse<Identifiable<IWorkExperience>>>(
			config.URIs.save_work_experience,
			exp,
			{ params: { candidateId, source } }
		);
		return response?.data ?? null;
	}

	async removeWorkExperience(id: string, candidateId: string, source?: CandidateType): Promise<string | null> {
		const response = await this.patchResource<null, TAResponse<{ id: string }>>(
			config.URIs.remove_work_experience.replace(/:workExpId/, id),
			null,
			{ params: { candidateId, source } }
		);
		return response?.data.id ?? null;
	}

	async editWorkExperience(
		id: string,
		exp: IWorkExperience,
		candidateId: string,
		source?: CandidateType
	): Promise<Identifiable<IWorkExperience> | null> {
		const response = await this.patchResource<IWorkExperience, TAResponse<Identifiable<IWorkExperience>>>(
			config.URIs.edit_work_experience.replace(/:workExpId/, id),
			exp,
			{ params: { candidateId, source } }
		);
		return response?.data ?? null;
	}

	//====Skills====

	async saveSkill(skill: ISkill, candidateId: string, source?: CandidateType): Promise<Identifiable<ISkill> | null> {
		const response = await this.patchResource<ISkill, TAResponse<Identifiable<ISkill>>>(
			config.URIs.save_skill,
			skill,
			{ params: { candidateId, source } }
		);
		return response?.data ?? null;
	}

	async removeSkill(id: string, candidateId: string, source?: CandidateType): Promise<string | null> {
		const response = await this.patchResource<null, TAResponse<{ id: string }>>(
			config.URIs.remove_skill.replace(/:skillId/, id),
			null,
			{ params: { candidateId, source } }
		);
		return response?.data.id ?? null;
	}

	async editSkill(
		id: string,
		skill: ISkill,
		candidateId: string,
		source?: CandidateType
	): Promise<Identifiable<ISkill> | null> {
		const response = await this.patchResource<ISkill, TAResponse<Identifiable<ISkill>>>(
			config.URIs.edit_skill.replace(/:skillId/, id),
			skill,
			{ params: { candidateId, source } }
		);
		return response?.data ?? null;
	}
	//====Online Presence====

	async saveOnlinePresence(
		onlinePresence: IOnlinePresence,
		candidateId: string,
		source?: CandidateType
	): Promise<IOnlinePresence | null> {
		const response = await this.patchResource<IOnlinePresence, TAResponse<IOnlinePresence>>(
			config.URIs.save_online_presence,
			onlinePresence,
			{ params: { candidateId, source } }
		);
		return response?.data ?? null;
	}
	//====CV====
	async addCandidateCv(fd: FormData, candidateId, source?: CandidateType): Promise<TAResponse<string> | null> {
		const response = await this.putResource<FormData, TAResponse<string>>(
			config.URIs.add_candidate_cv.replace(/:candidateId/g, candidateId),
			fd,
			{
				params: { source }
			}
		);
		return response ?? null;
	}
	async addCandidateBackofficeCv(
		fd: FormData,
		candidateId,
		source?: CandidateType
	): Promise<TAResponse<string> | null> {
		const response = await this.putResource<FormData, TAResponse<string>>(
			config.URIs.add_candidate_backoffice_cv.replace(/:candidateId/g, candidateId),
			fd,
			{ params: { source } }
		);
		return response ?? null;
	}

	async addDeveloperCv(fd: FormData, candidateId, source?: CandidateType): Promise<TAResponse<string> | null> {
		const response = await this.putResource<FormData, TAResponse<string>>(config.URIs.add_developer_cv, fd, {
			params: { candidateId, source }
		});
		return response ?? null;
	}

	async deleteDeveloperCv(candidateId: string, source?: CandidateType): Promise<boolean> {
		const response = await this.putResource<any, TAResponse<null>>(
			config.URIs.delete_developer_cv,
			{},
			{ params: { candidateId, source } }
		);
		return response?.success ?? false;
	}

	async addDeveloperAcpcCertificate(fd: FormData, candidateId): Promise<TAResponse<string> | null> {
		const response = await this.putResource<FormData, TAResponse<string>>(
			config.URIs.add_developer_acpc_certificate.replace(/:candidateId/g, candidateId),
			fd
		);
		return response ?? null;
	}

	async deleteDeveloperAcpcCertificate(candidateId: string): Promise<boolean> {
		const response = await this.putResource<any, TAResponse<null>>(
			config.URIs.delete_developer_acpc_certificate.replace(/:candidateId/g, candidateId),
			{}
		);
		return response?.success ?? false;
	}

	//====Minimum Salary====
	async saveMinimumSalary(minSalary: ISalary, candidateId: string, source?: CandidateType): Promise<ISalary | null> {
		const response = await this.patchResource<ISalary, TAResponse<ISalary>>(
			config.URIs.save_minimum_salary,
			minSalary,
			{ params: { candidateId, source } }
		);
		return response?.data ?? null;
	}

	//====Skills Of Interest====
	async saveSkillsOfInterest(soi: string[], candidateId: string, source?: CandidateType): Promise<string[] | null> {
		const response = await this.patchResource<{ skillsOfInterest: string[] }, TAResponse<string[]>>(
			config.URIs.save_skills_of_interest,
			{ skillsOfInterest: soi },
			{ params: { candidateId, source } }
		);
		return response?.data ?? null;
	}
	//====Additional Info====
	async editCandidateAdditionalInfo(
		info: ICandidateAdditionalInfo,
		candidateId,
		source?: CandidateType
	): Promise<ICandidateAdditionalInfo | null> {
		const response = await this.patchResource<ICandidateAdditionalInfo, TAResponse<any>>(
			config.URIs.update_candidate_additional_info,
			info,
			{ params: { candidateId, source } }
		);
		return response?.data ?? null;
	}

	//====Secondary phone numbers====
	async editCandidateSecondaryPhoneNumbers(
		secondaryPhoneNumbers: string[],
		candidateId,
		source?: CandidateType
	): Promise<string[] | null> {
		const response = await this.patchResource<string[], TAResponse<any>>(
			config.URIs.edit_candidate_secondary_phone_numbers,
			secondaryPhoneNumbers,
			{ params: { candidateId, source } }
		);
		return response?.data ?? null;
	}
	//====Secondary emails====
	async editCandidateEmails(
		secondaryEmails: string[],
		candidateId,
		source?: CandidateType
	): Promise<string[] | null> {
		const response = await this.patchResource<string[], TAResponse<any>>(
			config.URIs.edit_candidate_secondary_emails,
			secondaryEmails,
			{ params: { candidateId, source } }
		);
		return response?.data ?? null;
	}
}

export default new CandidatesService();
